import React, {useEffect, useState, useRef} from "react";
import Header from "../../../components/header/header";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faCog, faList, faEdit, faTrash} from "@fortawesome/free-solid-svg-icons";
import styles from "../../../styles/operation_results.module.css";
import axiosInstance from "../../../AxiosInstance";
import {Link} from "react-router-dom";
import Modal from "../../../components/modal/modal";
import {TextInput} from "../../../components/input/inputs";
import PageTitle from "../../../components/page_title/page_title";
import {useNavigate} from "react-router-dom";
import EmptyPlaceholder from "../../../components/empty_placeholder/empty_placeholder";
import {useTranslation} from "react-i18next";

function OperationGroupBox({operation, setOperationSettings, onContextMenu}) {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const handleContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e, operation);
    };

    const handleHistoryClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOperationSettings(operation.id);
        navigate(`/operations/history?group=${operation.id}`);
    };

    return (
        <Link
            to={`/operations/show?group=${operation.id}`}
            className={`${styles.group} rounded-2 shadow-sm`}
            onClick={() => setOperationSettings(operation.id)}
            onContextMenu={handleContextMenu}
        >
            <div className="d-flex justify-content-between">
                <FontAwesomeIcon icon={faList} color="#94A3B8"/>
                <FontAwesomeIcon icon={faArrowRight} color="#94A3B8"/>
            </div>
            <div className="text-start mt-2 fw-bold">{operation.name}</div>
            <div className='mt-3 d-flex' onClick={handleHistoryClick}>
                <div className={styles.group_history} role="button">{t('operations.showHistory')}</div>
            </div>
        </Link>
    );
}

export default function OperationGroups() {
    const {t} = useTranslation();
    const project = JSON.parse(localStorage.getItem('project'));

    const [form, setForm] = useState({project_id: project?.id});
    const [showModal, setShowModal] = useState(false);
    const [operations, setOperations] = useState([]);
    const navigate = useNavigate();

    // Context menu state
    const [contextMenu, setContextMenu] = useState({show: false, x: 0, y: 0, operation: null});
    const contextMenuRef = useRef(null);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({id: null, name: ''});

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let isMounted = true;
        fetchOperationGroups(isMounted);
        return () => {
            isMounted = false;
        };
    }, []);

    // Close context menu on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                setContextMenu({show: false, x: 0, y: 0, operation: null});
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchOperationGroups = (isMounted = true) => {
        if (!project?.id) return;
        axiosInstance.get('operation_group/?project_id=' + project.id).then((response) => {
            if (isMounted) {
                setOperations(response.data);
            }
        });
    };

    const createOperationGroup = () => {
        axiosInstance.post('operation_group/', form).then((response) => {
            const newGroupId = response.data.id;
            setOperationSettings(newGroupId);
            // Pasar el ID por URL para evitar race condition con localStorage
            navigate(`/operations/show?group=${newGroupId}`);
        }).catch((err) => {
            console.error('Error creating operation group:', err);
        });
        setShowModal(false);
    };

    const setOperationSettings = (id) => {
        // Solo guardar si es un ID válido
        if (id && id !== 'undefined' && id !== 'null') {
            localStorage.setItem('operation_group_id', id);
        }
    };

    const handleContextMenu = (e, operation) => {
        setContextMenu({
            show: true,
            x: e.clientX,
            y: e.clientY,
            operation: operation
        });
    };

    const handleEdit = () => {
        setEditForm({id: contextMenu.operation.id, name: contextMenu.operation.name});
        setShowEditModal(true);
        setContextMenu({show: false, x: 0, y: 0, operation: null});
    };

    const handleDelete = () => {
        setSelectedOperation(contextMenu.operation);
        setShowDeleteModal(true);
        setContextMenu({show: false, x: 0, y: 0, operation: null});
    };

    const updateOperationGroup = () => {
        axiosInstance.put('operation_group/', {id: editForm.id, name: editForm.name}).then((response) => {
            fetchOperationGroups();
            setShowEditModal(false);
            setEditForm({id: null, name: ''});
        }).catch((err) => {
            console.error('Error updating operation group:', err);
        });
    };

    const deleteOperationGroup = () => {
        setDeleting(true);
        axiosInstance.delete(`operation_group_delete/${selectedOperation.id}/`).then((response) => {
            fetchOperationGroups();
            setShowDeleteModal(false);
            setSelectedOperation(null);
            setDeleting(false);
        }).catch((err) => {
            console.error('Error deleting operation group:', err);
            setDeleting(false);
        });
    };

    return (
        <div className={`${styles.dashboard_container}`}>
            <Header sidebarIndex={4}/>

            {/* Context Menu */}
            {contextMenu.show && (
                <div
                    ref={contextMenuRef}
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        backgroundColor: '#FFF',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 100000
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s'
                        }}
                        onClick={handleEdit}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <FontAwesomeIcon icon={faEdit} style={{marginRight: '10px', color: '#64748B'}}/>
                        <span style={{color: '#334155'}}>{t('common.edit')}</span>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s'
                        }}
                        onClick={handleDelete}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <FontAwesomeIcon icon={faTrash} style={{marginRight: '10px', color: '#EF4444'}}/>
                        <span style={{color: '#EF4444'}}>{t('common.delete')}</span>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            <Modal show={showModal} setter={setShowModal} name={t('operations.createGroup')} buttonName={t('common.save')} onClick={createOperationGroup}>
                <div>
                    <TextInput form={form} setForm={setForm} name={'name'} placeholder={t('operations.operationName')}/>
                </div>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} setter={setShowEditModal} name={t('common.edit')} buttonName={t('common.save')} onClick={updateOperationGroup}>
                <div>
                    <TextInput form={editForm} setForm={setEditForm} name={'name'} placeholder={t('operations.operationName')}/>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                setter={setShowDeleteModal}
                name={t('operations.deleteGroupTitle')}
                buttonName={deleting ? t('common.deleting') : t('common.delete')}
                onClick={deleteOperationGroup}
                danger={true}
            >
                <div style={{textAlign: 'center', padding: '10px 0'}}>
                    <p style={{marginBottom: '10px'}}>{t('operations.deleteGroupConfirm')}</p>
                    <p style={{fontWeight: 'bold', color: '#334155'}}>{selectedOperation?.name}</p>
                    <p style={{color: '#64748B', fontSize: '14px', marginTop: '10px'}}>{t('operations.header.deleteGroupDesc')}</p>
                </div>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('operations.groupsTitle')} openModal={setShowModal} createName={t('operations.groupsTitle')}>
                    {false && <div className={`${styles.secondary_button} d-flex flex-row rounded-3 me-3`}>
                        <FontAwesomeIcon icon={faCog} color="#1F2A37"/>
                        <div>Quick Operations</div>
                    </div>}
                </PageTitle>

                {operations.length > 0 ?
                    <div className={`${styles.group_container} mt-3`}>
                        {operations.map((operation) => (
                            <OperationGroupBox
                                key={operation.id}
                                operation={operation}
                                setOperationSettings={setOperationSettings}
                                onContextMenu={handleContextMenu}
                            />
                        ))}
                    </div>
                    :
                    <EmptyPlaceholder mod={'alt'} type={t('operations.groupsType')} width={'452px'}/>
                }
            </div>
        </div>
    );
}